import React, { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Search, Edit, Trash2, UserPlus } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useFirebase } from "@/contexts/FirebaseContext";
import { 
  getAuth, 
  createUserWithEmailAndPassword,
  updateProfile,
  deleteUser
} from "firebase/auth";
import { doc, setDoc, deleteDoc, getFirestore, collection, getDocs } from "firebase/firestore";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Area {
  id: string;
  name: string;
  postcode: string;
}

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  licenseNumber: string;
  experience: string;
  carModel: string;
  specialties: string[];
  availability: string;
  area?: string;
  students: number;
  status: "active" | "inactive" | "on leave";
  createdAt: string;
  role: string;
}

const InstructorsManagement = () => {
  const { toast } = useToast();
  const { getAllInstructors } = useFirebase();
  const [searchTerm, setSearchTerm] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteInstructorId, setDeleteInstructorId] = useState<string | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedInstructor, setSelectedInstructor] = useState<Instructor | null>(null);
  const [newInstructor, setNewInstructor] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    phone: "",
    licenseNumber: "",
    experience: "1",
    carModel: "",
    specialties: "",
    availability: "Weekdays",
    area: "",
  });
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    const fetchInstructors = async () => {
      try {
        const firestoreInstructors = await getAllInstructors();
        setInstructors(firestoreInstructors as Instructor[]);
      } catch (error) {
        console.error("Error fetching instructors:", error);
        toast({
          title: "Error",
          description: "Failed to load instructors",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    const fetchAreas = async () => {
      try {
        const db = getFirestore();
        const areasRef = collection(db, 'areas');
        const snapshot = await getDocs(areasRef);
        const areasData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Area[];
        setAreas(areasData);
      } catch (error) {
        console.error("Error fetching areas:", error);
      }
    };

    fetchInstructors();
    fetchAreas();
  }, [getAllInstructors, toast]);

  const filteredInstructors = instructors.filter(
    (instructor) =>
      instructor.firstName?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
      instructor.lastName?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
      instructor.email?.toLowerCase()?.includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (selectedInstructor) {
      setSelectedInstructor({ ...selectedInstructor, [name]: value });
    } else {
    setNewInstructor({ ...newInstructor, [name]: value });
    }
  };

  const handleEditInstructor = (instructor: Instructor) => {
    setSelectedInstructor(instructor);
    setIsEditDialogOpen(true);
  };

  const handleUpdateInstructor = async () => {
    if (!selectedInstructor) return;

    try {
      const db = getFirestore();
      const instructorRef = doc(db, "users", selectedInstructor.id);
      
      // Handle specialties field - convert string to array if needed
      let specialties: string[] = ["General"];
      const specialtiesValue = selectedInstructor.specialties as any;
      if (typeof specialtiesValue === 'string') {
        specialties = specialtiesValue.split(',').map(s => s.trim()).filter(s => s.length > 0);
      } else if (Array.isArray(specialtiesValue)) {
        specialties = specialtiesValue;
      }
      
      const updateData = {
        firstName: selectedInstructor.firstName || "",
        lastName: selectedInstructor.lastName || "",
        email: selectedInstructor.email || "",
        phone: selectedInstructor.phone || "N/A",
        licenseNumber: selectedInstructor.licenseNumber || "N/A",
        experience: selectedInstructor.experience || "1",
        carModel: selectedInstructor.carModel || "N/A",
        specialties: specialties,
        availability: selectedInstructor.availability || "Weekdays",
        area: selectedInstructor.area || "Not specified",
        role: selectedInstructor.role || 'instructor',
        updatedAt: new Date().toISOString()
      };

      await setDoc(instructorRef, updateData, { merge: true });

      toast({
        title: "Success",
        description: "Instructor updated successfully",
      });
      
      // Refresh the instructors list
      const updatedInstructors = await getAllInstructors();
      setInstructors(updatedInstructors as Instructor[]);

      setIsEditDialogOpen(false);
      setSelectedInstructor(null);
    } catch (error) {
      console.error("Error updating instructor:", error);
      toast({
        title: "Error",
        description: "Failed to update instructor",
        variant: "destructive",
      });
    }
  };

  const handleDeleteInstructor = (instructorId: string) => {
    setDeleteInstructorId(instructorId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteInstructor = async () => {
    if (!deleteInstructorId) return;
    
    try {
      const auth = getAuth();
      const db = getFirestore();
      
      // Delete from Firestore
      await deleteDoc(doc(db, "users", deleteInstructorId));
      
      // Delete from Firebase Auth
      const user = auth.currentUser;
      if (user && user.uid === deleteInstructorId) {
        await deleteUser(user);
      }
      
      // Update local state
      setInstructors(instructors.filter(instructor => instructor.id !== deleteInstructorId));
      
      toast({
        title: "Success",
        description: "Instructor deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting instructor:", error);
      toast({
        title: "Error",
        description: "Failed to delete instructor",
        variant: "destructive",
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setDeleteInstructorId(null);
    }
  };

  const handleCreateInstructor = async () => {
    if (!newInstructor.firstName || !newInstructor.lastName || !newInstructor.email || !newInstructor.password) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCreating(true);
      // Create the user in Firebase Auth
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newInstructor.email, 
        newInstructor.password
      );
      
      if (!userCredential.user) {
        throw new Error("Failed to create authentication user");
      }

      // Update the user's display name
      await updateProfile(userCredential.user, {
        displayName: `${newInstructor.firstName} ${newInstructor.lastName}`
      });

      // Create the instructor data
      const instructorData = {
        id: userCredential.user.uid,
        firstName: newInstructor.firstName,
        lastName: newInstructor.lastName,
        email: newInstructor.email,
        phone: newInstructor.phone || "N/A",
        licenseNumber: newInstructor.licenseNumber || "N/A",
        experience: newInstructor.experience + " years",
        carModel: newInstructor.carModel || "N/A",
        specialties: newInstructor.specialties ? [newInstructor.specialties] : ["General"],
        availability: newInstructor.availability,
        area: newInstructor.area || "Not specified",
        students: 0,
        status: "active",
        role: "instructor", // Explicitly set role as instructor
        createdAt: new Date().toISOString()
      };

      // Store the instructor data in Firestore with explicit role
      const db = getFirestore();
      await setDoc(doc(db, "users", userCredential.user.uid), instructorData);
      
      toast({
        title: "Success",
        description: `Instructor ${newInstructor.firstName} ${newInstructor.lastName} created successfully`,
      });
      
      // Refresh the instructors list
      const updatedInstructors = await getAllInstructors();
      setInstructors(updatedInstructors as Instructor[]);
      
      // Reset the form
      setNewInstructor({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        phone: "",
        licenseNumber: "",
        experience: "1",
        carModel: "",
        specialties: "",
        availability: "Weekdays",
        area: "",
      });
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error creating instructor:", error);
      toast({
        title: "Error",
        description: "Failed to create instructor. Email may already be in use.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <DashboardLayout role="admin">
      <div className="mb-8 space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Instructors Management</h1>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="h-4 w-4 mr-2" />
                Add New Instructor
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Instructor</DialogTitle>
              </DialogHeader>
              
              <ScrollArea className="max-h-[70vh] px-1">
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name*</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={newInstructor.firstName}
                        onChange={handleInputChange}
                        placeholder="John"
                        className="bg-background"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name*</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={newInstructor.lastName}
                        onChange={handleInputChange}
                        placeholder="Doe"
                        className="bg-background"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email*</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={newInstructor.email}
                      onChange={handleInputChange}
                      placeholder="john.doe@example.com"
                      className="bg-background"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">Password*</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={newInstructor.password}
                      onChange={handleInputChange}
                      className="bg-background"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={newInstructor.phone}
                      onChange={handleInputChange}
                      placeholder="(123) 456-7890"
                      className="bg-background"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="licenseNumber">License Number</Label>
                    <Input
                      id="licenseNumber"
                      name="licenseNumber"
                      value={newInstructor.licenseNumber}
                      onChange={handleInputChange}
                      placeholder="DL12345678"
                      className="bg-background"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="experience">Years of Experience</Label>
                    <select
                      id="experience"
                      name="experience"
                      className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      value={newInstructor.experience}
                      onChange={handleInputChange}
                    >
                      <option value="1">1 year</option>
                      <option value="2">2 years</option>
                      <option value="3">3 years</option>
                      <option value="5">5+ years</option>
                      <option value="10">10+ years</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="carModel">Car Model</Label>
                    <Input
                      id="carModel"
                      name="carModel"
                      value={newInstructor.carModel}
                      onChange={handleInputChange}
                      placeholder="Toyota Corolla"
                      className="bg-background"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="specialties">Specialty</Label>
                    <Input
                      id="specialties"
                      name="specialties"
                      value={newInstructor.specialties}
                      onChange={handleInputChange}
                      placeholder="Defensive Driving"
                      className="bg-background"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="availability">Availability</Label>
                    <select
                      id="availability"
                      name="availability"
                      className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      value={newInstructor.availability}
                      onChange={handleInputChange}
                    >
                      <option value="Weekdays">Weekdays</option>
                      <option value="Weekends">Weekends</option>
                      <option value="Evenings">Evenings</option>
                      <option value="Full-time">Full-time</option>
                    </select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="area">Area Covered</Label>
                    <select
                      id="area"
                      name="area"
                      className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                      value={newInstructor.area}
                      onChange={handleInputChange}
                    >
                      <option value="">Select an area</option>
                      {areas.map((area) => (
                        <option key={area.id} value={area.name}>
                          {area.name} ({area.postcode})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </ScrollArea>
              
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isCreating}>
                  Cancel
                </Button>
                <Button onClick={handleCreateInstructor} disabled={isCreating}>
                  {isCreating ? (
                    <>
                      <span className="mr-2">Creating...</span>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    </>
                  ) : (
                    "Create Instructor"
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
        
        <p className="text-gray-600">
          Manage instructor profiles, schedules, and student assignments.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-1">
          <CardTitle>All Instructors</CardTitle>
          <div className="flex items-center w-full max-w-sm space-x-2 mt-4">
            <Input
              placeholder="Search instructors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            <Button variant="outline" size="icon">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <p>Loading instructors...</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Experience</TableHead>
                  <TableHead>Specialty</TableHead>
                  <TableHead>Area</TableHead>
                  <TableHead>Students</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInstructors.length > 0 ? (
                  filteredInstructors.map((instructor) => (
                    <TableRow key={instructor.id}>
                      <TableCell>
                        <div className="font-medium">
                          {instructor.firstName} {instructor.lastName}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-500">{instructor.email}</div>
                        <div className="text-sm">{instructor.phone}</div>
                      </TableCell>
                      <TableCell>{instructor.experience}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(instructor.specialties) && instructor.specialties.map((specialty, index) => (
                            <Badge key={index} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              {specialty}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {instructor.area || "Not specified"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <span className="font-medium">{instructor.students || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            instructor.status === "active"
                              ? "bg-green-500"
                              : instructor.status === "on leave"
                              ? "bg-amber-500"
                              : "bg-gray-500"
                          }
                        >
                          {instructor.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleEditInstructor(instructor)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteInstructor(instructor.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-4">
                      No instructors found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Instructor</DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="max-h-[70vh] px-1">
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name*</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={selectedInstructor?.firstName || ""}
                    onChange={handleInputChange}
                    placeholder="John"
                    className="bg-background"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name*</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={selectedInstructor?.lastName || ""}
                    onChange={handleInputChange}
                    placeholder="Doe"
                    className="bg-background"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="email">Email*</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={selectedInstructor?.email || ""}
                  onChange={handleInputChange}
                  placeholder="john.doe@example.com"
                  className="bg-background"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={selectedInstructor?.phone || ""}
                  onChange={handleInputChange}
                  placeholder="(123) 456-7890"
                  className="bg-background"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="licenseNumber">License Number</Label>
                <Input
                  id="licenseNumber"
                  name="licenseNumber"
                  value={selectedInstructor?.licenseNumber || ""}
                  onChange={handleInputChange}
                  placeholder="DL12345678"
                  className="bg-background"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="experience">Years of Experience</Label>
                <select
                  id="experience"
                  name="experience"
                  className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  value={selectedInstructor?.experience || "1"}
                  onChange={handleInputChange}
                >
                  <option value="1">1 year</option>
                  <option value="2">2 years</option>
                  <option value="3">3 years</option>
                  <option value="5">5+ years</option>
                  <option value="10">10+ years</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="carModel">Car Model</Label>
                <Input
                  id="carModel"
                  name="carModel"
                  value={selectedInstructor?.carModel || ""}
                  onChange={handleInputChange}
                  placeholder="Toyota Corolla"
                  className="bg-background"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="specialties">Specialty</Label>
                <Input
                  id="specialties"
                  name="specialties"
                  value={Array.isArray(selectedInstructor?.specialties) ? selectedInstructor.specialties.join(", ") : (selectedInstructor?.specialties || "")}
                  onChange={handleInputChange}
                  placeholder="Defensive Driving"
                  className="bg-background"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="availability">Availability</Label>
                <select
                  id="availability"
                  name="availability"
                  className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  value={selectedInstructor?.availability || "Weekdays"}
                  onChange={handleInputChange}
                >
                  <option value="Weekdays">Weekdays</option>
                  <option value="Weekends">Weekends</option>
                  <option value="Evenings">Evenings</option>
                  <option value="Full-time">Full-time</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="area">Area Covered</Label>
                <select
                  id="area"
                  name="area"
                  className="w-full h-10 px-3 py-2 bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  value={selectedInstructor?.area || ""}
                  onChange={handleInputChange}
                >
                  <option value="">Select an area</option>
                  {areas.map((area) => (
                    <option key={area.id} value={area.name}>
                      {area.name} ({area.postcode})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </ScrollArea>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateInstructor}>Update Instructor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the instructor
              and remove their data from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteInstructor}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </DashboardLayout>
  );
};

export default InstructorsManagement;
